import { useTranslation } from 'react-i18next'
import { AuditFragment, ChargeCategoryAuditFragment, CreditType, ReservationStateFragment, VatAuditFragment } from 'src/api/api.generated'
import useFormatDate from 'src/app/hooks/current-locale/use-format-date'
import translateEntityName, { DB_ENTITIES } from '../utils/translate-entity-name'
import { AUDIT_OPERATIONS } from '../utils/translate-operation'
import useFormatCurrency from 'src/app/hooks/current-locale/use-format-currency'
import getFiscalTypeDescription from '../../accounting/documents/documents-utils'

export default function useGetContent({
  audit,
  reservationStates,
  chargeCategories,
  vats,
}: {
  audit: AuditFragment
  reservationStates: ReservationStateFragment[]
  chargeCategories: ChargeCategoryAuditFragment[]
  vats: VatAuditFragment[]
}) {
  const formatDate = useFormatDate()
  const { formatCurrency } = useFormatCurrency()
  const { t } = useTranslation()
  let content: string | null = null

  const changes = audit.changes ?? {}

  if (audit.operation === AUDIT_OPERATIONS.delete) {
    content = `${t('pms.audits.grid.changes.delete', {
      date: formatDate(new Date(changes?.['auditable.modifiedOn']), 'Pp:ss'),
      entityName: translateEntityName({ entityName: audit.entityName, t }),
    })}`
  } else if (audit.entityName === DB_ENTITIES.Reservation) {
    const reservationState = reservationStates.find((state) => state.id === changes.reservationStateId)

    content = `${audit.operation === 'update' ? t('pms.audits.grid.changes.reservation.update') : t('pms.audits.grid.changes.reservation.insert')} ${
      changes.reservationCode ? `(${changes.reservationCode})` : ''
    } ${changes.isGroup ? t('pms.audits.grid.changes.reservation.insert.group') : ''}
${changes?.searchInfo?.start ? `${t('pms.booking.reservation.resource_time.start')}: ${formatDate(new Date(changes?.searchInfo?.start))}` : ''}${
      changes?.searchInfo?.end
        ? `
${t('pms.booking.reservation.resource_time.end')}: ${formatDate(new Date(changes?.searchInfo?.end))}`
        : ''
    }${
      changes?.searchInfo?.mainGuestName
        ? `
${t('pms.booking.reservation.reference.main_guest')}: ${changes?.searchInfo?.mainGuestName}`
        : ''
    }${
      changes?.searchInfo?.customerPersonName
        ? `
${t('pms.booking.reservation.reference.customer_person')}: ${changes?.searchInfo?.customerPersonName}`
        : ''
    }${
      changes?.searchInfo?.customerCompanyName
        ? `
${t('pms.booking.reservation.reference.customer_company')}: ${changes?.searchInfo?.customerCompanyName}`
        : ''
    }${
      changes?.searchInfo?.customerCompanyName && changes.credit
        ? `
${t('pms.audits.grid.changes.credit')}: ${t(`pms.booking.reservation_new.options.credit.${changes.credit as CreditType}`)}`
        : ''
    }${
      reservationState
        ? `
${t('pms.audits.grid.changes.reservation_state')}: ${reservationState.name}`
        : ''
    }${
      changes.policy
        ? `
${t('pms.booking.reservation.booking_policy')}: ${changes.policy.name}`
        : ''
    }`
  } else if (audit.entityName === DB_ENTITIES.Bill) {
    const description = changes?.reservationId?.some((resId: string) => changes?.description?.includes(resId)) ? null : changes.description

    content = `${audit.operation === AUDIT_OPERATIONS.update ? t('pms.audits.grid.changes.bill.update') : t('pms.audits.grid.changes.bill.insert')} ${
      changes?.searchInfo?.ownerName ? `${t('pms.audits.grid.changes.bill.of')} ${changes?.searchInfo?.ownerName}` : ''
    }
${
  changes.category
    ? `
${t('pms.audits.grid.changes.bill.category')}: ${changes.category}`
    : ''
}${
      description
        ? `
${t('pms.audits.grid.changes.bill.name')}: ${description}`
        : ''
    }${
      'open' in changes
        ? `
${t('pms.audits.grid.changes.bill.open')}: ${t(`pms.audits.grid.changes.bill.open.${changes.open as boolean}`)}`
        : ''
    }`
  } else if (audit.entityName === DB_ENTITIES.Production) {
    const chargeCategory = chargeCategories.find((charge) => charge.id === changes?.chargeCategoryId)
    const vat = vats.find((vatToCheck) => vatToCheck.id === changes?.vatId)

    content = `${audit.operation === AUDIT_OPERATIONS.update ? t('pms.audits.grid.changes.production.update') : t('pms.audits.grid.changes.production.insert')}
${
  'amount' in changes
    ? `
${t('pms.booking.reservation.productions_assigned.amount')}: ${formatCurrency(changes.amount, changes.currency ?? 'EUR')}`
    : ''
}${
      'quantity' in changes
        ? `
${t('pms.audits.grid.changes.production.quantity')}: ${changes.quantity}`
        : ''
    }${
      'total' in changes
        ? `
${t('pms.booking.reservation.productions_assigned.total')}: ${formatCurrency(changes.total, changes.currency ?? 'EUR')}`
        : ''
    }${
      changes.date
        ? `
${t('pms.booking.reservation.productions_assigned.date')}: ${formatDate(new Date(changes.date))}`
        : ''
    }${
      chargeCategory
        ? `
${t('pms.booking.reservation.productions_assigned.charge_category')}: ${chargeCategory.name}`
        : ''
    }${
      vat
        ? `
${t('pms.booking.reservation.productions_assigned.vat')}: ${vat.description}`
        : ''
    }${
      changes.description
        ? `
${t('pms.booking.reservation.productions_assigned.description')}: ${changes.description}`
        : ''
    }`
  } else if (audit.entityName === DB_ENTITIES.Document) {
    const isDraft = changes?.auditable?.state === 'DRAFT'
    content = `${audit.operation === AUDIT_OPERATIONS.update ? t('pms.audits.grid.changes.documment.update') : t('pms.audits.grid.changes.documment.insert')}${
      'numberExtended' in changes ? ` (${changes.numberExtended})` : ''
    }${isDraft ? ` ${t('pms.audits.grid.changes.documment.draft')}` : ''}
${
  changes.fiscalType
    ? `
${t('pms.audits.grid.changes.documment.type')}: ${getFiscalTypeDescription(changes.fiscalType, t)}`
    : ''
}${
      'totalAmount' in changes
        ? `
${t('commons.document_total')}: ${formatCurrency(changes.totalAmount, changes.currency ?? 'EUR')}`
        : ''
    }${
      'unpaidAmount' in changes
        ? `
${t('pms.audits.grid.changes.documment.to_be_paid')}: ${formatCurrency(changes.unpaidAmount, changes.currency ?? 'EUR')}`
        : ''
    }${
      'unpaidAmount' in changes && 'totalAmount' in changes
        ? `
${t('pms.audits.grid.changes.documment.paid')}: ${formatCurrency(changes.totalAmount - changes.unpaidAmount, changes.currency ?? 'EUR')}`
        : ''
    }${
      'vatAmount' in changes
        ? `
${t('pms.audits.grid.changes.documment.vat_amount')}: ${formatCurrency(changes.vatAmount, changes.currency ?? 'EUR')}`
        : ''
    }${
      'netAmount' in changes
        ? `
${t('pms.audits.grid.changes.documment.net_amount')}: ${formatCurrency(changes.netAmount, changes.currency ?? 'EUR')}`
        : ''
    }`
  }

  return content
}
